import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { ChannelDot } from '@/components/console/ChannelDot';
import { StateBadge } from '@/components/console/StateBadge';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { useActivityStore } from '@/stores/activityStore';
import { useAuthStore } from '@/stores/authStore';
import { getConversationLabel, getConversationPreview } from '@/utils/conversationLabel';
import { formatRelative } from '@/utils/formatters';
import { countMyResolvedToday, type ResolvedToday } from '@/utils/resolvedToday';
import { useConsole } from '@/console/ConsoleContext';
import { useFolderCounts } from '@/console/useFolderCounts';
import type { Conversation } from '@/types';

export const MyDayPage: React.FC = () => {
  const { t, urgencyLabel } = useT();
  const navigate = useNavigate();
  const conversations = useConversationStore((s) => s.conversations);
  const activity = useActivityStore((s) => s.items);
  const hiddenSessions = useConversationStore((s) => s.hiddenSessions);
  const spamTombstones = useConversationStore((s) => s.spamTombstones);
  const user = useAuthStore((s) => s.user);
  const { selectFolder } = useConsole();
  const { countOf } = useFolderCounts();

  // "My queue" is the My conversations folder: make sure the shell has it loaded (it lists whichever folder was last
  // selected), without changing what the Conversations page opens on (that is the URL's).
  useEffect(() => {
    selectFolder('mine');
  }, [selectFolder]);

  const [resolved, setResolved] = useState<ResolvedToday | null>(null);

  // Resolved today = MY conversations that closed today, read from the closed folder. Approximate (see
  // countMyResolvedToday): the screen says so. A failed read is "not available", never a smaller number.
  useEffect(() => {
    const me = user?.agentId;
    setResolved(null);
    if (!me) return;
    let cancelled = false;
    (async () => {
      try {
        const answer = await countMyResolvedToday(me);
        if (!cancelled) setResolved(answer);
      } catch {
        if (!cancelled) setResolved(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.agentId, user?.tenantId]);

  const open = useMemo(() => conversations.filter((c) => c.state !== 'CLOSED'), [conversations]);
  // Mine = the rows whose OWNER (the wire `ownerAgentId`, not the live controller) is me. There is no fallback to "show every
  // open conversation when I own none": an agent who owns nothing has an empty queue, and the page says so.
  const mine = useMemo(
    () => open.filter((c) => !!c.ownerAgentId && !!user?.agentId && c.ownerAgentId === user.agentId).slice(0, 6),
    [open, user?.agentId],
  );
  const assignedCount = countOf('mine');
  const atRisk = open.filter((c) => c.slaUrgency !== 'NORMAL');
  // The feed names conversations; one that has since been hidden from this viewer (withdrawn, 404, spam) must not be named.
  const visibleActivity = useMemo(
    () => activity.filter((a) => !a.sessionId || (!(a.sessionId in hiddenSessions) && !spamTombstones[a.sessionId])),
    [activity, hiddenSessions, spamTombstones],
  );

  const firstName = (user?.displayName || '').trim().split(/\s+/)[0] || t('role_agent');
  const greetingKey = new Date().getHours() < 12 ? 'greeting_morning' : 'greeting_day';

  // The URL owns the selection now, so navigating IS opening it — and this inherits
  // ConversationsPage's hydrate-on-demand, which matters for a row the store may not hold.
  const openConversation = (id: string) => {
    navigate(`/conversations/${encodeURIComponent(id)}`);
  };

  const stats: Array<{
    icon: string;
    color: string;
    soft: string;
    val: React.ReactNode;
    label: string;
    na?: boolean;
    /** An honest caveat shown under the label (the figure is approximate). */
    hint?: string;
    testId?: string;
  }> = [
    {
      icon: 'chat',
      color: 'var(--accent)',
      soft: 'var(--accent-soft)',
      val: assignedCount ?? t('na_short'),
      label: t('stat_assigned'),
      na: assignedCount === null,
      testId: 'stat-assigned',
    },
    {
      icon: 'check',
      color: 'var(--success)',
      soft: 'var(--success-soft)',
      val: resolved === null ? t('na_short') : resolved.complete ? resolved.count : `${resolved.count}+`,
      label: t('stat_resolved'),
      na: resolved === null,
      hint: t('stat_resolved_hint'),
      testId: 'stat-resolved',
    },
    { icon: 'clock', color: 'var(--info)', soft: 'var(--info-soft)', val: t('na_short'), label: t('stat_response'), na: true },
    { icon: 'star', color: 'var(--warning)', soft: 'var(--warning-soft)', val: t('na_short'), label: t('stat_csat'), na: true },
  ];

  return (
    <div className="ac-page ac-fade-up" data-testid="myday-page">
      <div className="ac-page-head">
        <h1 className="ac-page-title">{t(greetingKey, { name: firstName })}</h1>
        <div className="ac-page-sub">{t('greeting_sub')}</div>
      </div>

      <div className="ac-stat-grid ac-stagger">
        {stats.map((s, i) => (
          <div key={i} className="ac-card ac-stat">
            <div className="ac-stat-ico" style={{ background: s.soft, color: s.color }}>
              <Icon name={s.icon} size={20} />
            </div>
            <div>
              <div className={'ac-stat-val' + (s.na ? ' na' : '')} data-testid={s.testId ? `${s.testId}-value` : undefined}>
                {s.val}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span className="ac-stat-label">{s.label}</span>
              </div>
              {s.hint && (
                <div className="ac-stat-hint" data-testid={s.testId ? `${s.testId}-hint` : undefined}>
                  {s.hint}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {atRisk.length > 0 && (
        <div className="ac-sla-banner ac-fade-up" data-testid="sla-banner">
          <div className="ac-sla-ico">
            <Icon name="alert" size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{t('sla_atrisk')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
              {t('sla_atrisk_sub', { n: atRisk.length })}
            </div>
          </div>
          <button className="ac-btn ghost sm" onClick={() => navigate('/conversations')}>
            {t('view_all')}
          </button>
        </div>
      )}

      <div className="ac-myday-grid">
        {/* My open conversations */}
        <div className="ac-card">
          <div className="ac-section-head">
            <div className="ac-section-title">{t('my_queue')}</div>
            <button className="ac-link" onClick={() => navigate('/conversations')}>
              {t('view_all')} <Icon name="arrow" size={13} className="ac-flip" />
            </button>
          </div>
          <div>
            {mine.length === 0 ? (
              <div className="ac-act-empty">{t('dir_empty')}</div>
            ) : (
              mine.map((c: Conversation) => (
                <div key={c.sessionId} className="ac-mini-row" onClick={() => openConversation(c.sessionId)} data-testid={`myday-conv-${c.sessionId}`}>
                  <Avatar name={getConversationLabel(c)} seed={c.sessionId} size={38} className="ac-conv-av">
                    <ChannelDot channel={c.channel} size={16} />
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ac-mini-name">{getConversationLabel(c)}</div>
                    <div className="ac-mini-prev">{getConversationPreview(c)}</div>
                  </div>
                  <div className="ac-mini-meta">
                    <StateBadge state={c.state} />
                    {c.slaUrgency !== 'NORMAL' && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: c.slaUrgency === 'CRITICAL' ? 'var(--danger)' : 'var(--warning)',
                        }}
                      >
                        {urgencyLabel(c.slaUrgency)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Live activity */}
        <div className="ac-card">
          <div className="ac-section-head">
            <div className="ac-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="ac-status-dot online ac-pulse" style={{ width: 8, height: 8, borderRadius: '50%', color: 'var(--success)' }} />
              {t('activity')}
            </div>
          </div>
          <div>
            {visibleActivity.length === 0 ? (
              <div className="ac-act-empty">{t('activity_empty')}</div>
            ) : (
              visibleActivity.slice(0, 10).map((a) => (
                <div key={a.id} className="ac-act">
                  <div className="ac-act-ico" style={{ background: a.color + '22', color: a.color }}>
                    <Icon name={a.icon} size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="ac-act-text">{t(a.textKey, { name: a.name })}</div>
                    <div className="ac-act-time">{formatRelative(a.at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
